import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { sdk } from '@/graphql/client'
import { useEventsStore } from './events'

export interface Ticket {
    id: string
    code: string
    used: boolean
    usedAt?: string | null
    usedBy?: { name: string } | null
    ticketNumber?: string | null
    firstName?: string | null
    lastName?: string | null
    updatedAt: string
    isOnline?: boolean | null
    price?: { name: string } | null
}

export interface TicketsetWithList {
    id: string | number
    last_updated_at: number
    list: Ticket[]
}

export const useTicketsStore = defineStore('tickets', () => {
    const tickets = ref<TicketsetWithList[]>([])
    const lastCode = ref('')
    const updateTimer = ref<number | null>(null)

    function setLastCode(value: string) {
        lastCode.value = value
    }

    function clearTickets() {
        tickets.value = []
    }

    function setTicketsForSet(payload: { id: string | number; list: Ticket[] }) {
        const last = payload.list.reduce<number>((acc, t) => {
            const pt = new Date(t.updatedAt).getTime()
            return pt > acc ? pt : acc
        }, 0)
        tickets.value = [...tickets.value, { id: payload.id, last_updated_at: last, list: payload.list }]
    }

    function updateTicketsForSet(payload: { id: string | number; list: Ticket[] }) {
        tickets.value = tickets.value.map(ticketset => {
            if (ticketset.id !== payload.id) return ticketset
            const list = [...ticketset.list]
            payload.list.forEach(item => {
                const idx = list.findIndex(t => t.id === item.id)
                if (idx >= 0) {
                    list[idx] = { ...list[idx], ...item }
                } else {
                    list.push({ ...item })
                }
            })
            const last = list.reduce<number>((acc, t) => {
                const pt = new Date(t.updatedAt).getTime()
                return pt > acc ? pt : acc
            }, ticketset.last_updated_at)
            return { ...ticketset, list, last_updated_at: last }
        })
    }

    function useCode(payload: { ticketsetId: number | string; code: string; usedByName?: string | null }) {
        tickets.value = tickets.value.map(ticketset => {
            if (String(ticketset.id) !== String(payload.ticketsetId)) return ticketset
            return {
                ...ticketset,
                list: ticketset.list.map(t =>
                    t.code === payload.code
                        ? {
                              ...t,
                              used: true,
                              usedAt: new Date().toISOString(),
                              usedBy: payload.usedByName ? { name: payload.usedByName } : (t.usedBy ?? null)
                          }
                        : t
                )
            }
        })
    }

    function ingestTicketsetList(
        raw:
            | ReadonlyArray<{ ticketsetId?: number | null; tickets?: ReadonlyArray<unknown> | null } | null>
            | null
            | undefined
    ) {
        for (const ts of raw ?? []) {
            if (!ts || ts.ticketsetId === null || ts.ticketsetId === undefined) continue
            setTicketsForSet({
                id: ts.ticketsetId,
                list: (ts.tickets ?? []) as Ticket[]
            })
        }
    }

    async function loadTickets(ticketsetIds: number[]) {
        const result = await sdk.Tickets({ ticketsetIds })
        const list = result.mobile?.tickets ?? []
        ingestTicketsetList(list)
        return list
    }

    async function loadTicketsZones() {
        const eventsStore = useEventsStore()
        const result = await sdk.Tickets({ zoneIds: eventsStore.activeZones })
        const list = result.mobile?.tickets ?? []
        ingestTicketsetList(list)
        return list
    }

    function updateLoopStop() {
        if (updateTimer.value !== null) {
            window.clearInterval(updateTimer.value)
            updateTimer.value = null
        }
    }

    function updateLoopStart() {
        updateLoopStop()
        const eventsStore = useEventsStore()
        const id = window.setInterval(async () => {
            // Server expects `last` as ISO datetime string (calls .split() on it).
            const ticketsetsArg = tickets.value.map(ts =>
                JSON.stringify({
                    id: ts.id,
                    last: ts.last_updated_at ? new Date(ts.last_updated_at).toISOString() : ''
                })
            )
            try {
                const result = await sdk.TicketsUpdated({
                    ticketsets: ticketsetsArg,
                    zoneIds: eventsStore.activeZones
                })
                const list = result.mobile?.ticketsUpdated ?? []
                for (const ts of list) {
                    if (!ts || ts.ticketsetId === null || ts.ticketsetId === undefined) continue
                    updateTicketsForSet({ id: ts.ticketsetId, list: (ts.tickets ?? []) as Ticket[] })
                }
            } catch {
                // ignore network errors during polling
            }
        }, 10_000)
        updateTimer.value = id
    }

    // Getters
    const getTicketByCode = computed(() => (code: string): [Ticket | null, TicketsetWithList | null] => {
        let ret: [Ticket | null, TicketsetWithList | null] = [null, null]
        for (const ticketset of tickets.value) {
            const t = ticketset.list.find(ticket => {
                const shortLen = Math.min(ticket.code.length, code.length)
                return ticket.code.substring(0, shortLen) === code.substring(0, shortLen)
            })
            if (t) {
                ret = [t, ticketset]
            }
        }
        return ret
    })

    const getUsedTicketsCount = computed(() =>
        tickets.value.reduce((acc, ts) => acc + ts.list.filter(t => t.used).length, 0)
    )

    const getAllTicketsCount = computed(() => tickets.value.reduce((acc, ts) => acc + ts.list.length, 0))

    const activeTicketsetById = computed(() => (id: string | number) => {
        const eventsStore = useEventsStore()
        const idStr = String(id)

        if (eventsStore.activeZones.length) {
            for (const event of eventsStore.events) {
                for (const ts of event.ticketsets) {
                    if (String(ts.id) === idStr) return ts
                }
            }
            return null
        }

        for (const eventId of eventsStore.activeEvents) {
            const event = eventsStore.events.find(e => e.id === eventId)
            if (!event) continue
            for (const ts of event.ticketsets) {
                if (String(ts.id) === idStr) return ts
            }
        }
        return null
    })

    const activeEventByTicketsetId = computed(() => (id: string | number) => {
        const eventsStore = useEventsStore()
        const idStr = String(id)
        for (const eventId of eventsStore.activeEvents) {
            const event = eventsStore.events.find(e => e.id === eventId)
            if (!event) continue
            if (event.ticketsets.some(ts => String(ts.id) === idStr)) return event
        }
        return null
    })

    return {
        tickets,
        lastCode,
        updateTimer,
        setLastCode,
        clearTickets,
        setTicketsForSet,
        updateTicketsForSet,
        useCode,
        loadTickets,
        loadTicketsZones,
        updateLoopStart,
        updateLoopStop,
        getTicketByCode,
        getUsedTicketsCount,
        getAllTicketsCount,
        activeTicketsetById,
        activeEventByTicketsetId
    }
})
